import { WarningRounded } from "@mui/icons-material";
import { Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Stack } from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useCallback, useContext, useEffect, useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { AxiosError } from "axios";
import { GlobalContext } from "../../../../../../util/state/GlobalContextProvider";
import CustomLoader from "../../../../../../util/component/CustomLoader";
import { BookingStatus, FirstInvoiceRecipient, ReactQueryKey, RentalType } from "../../../../types";
import {
    AncillarySubtype,
    checkStopSell,
    IGetCompanyReturn,
    useBookingByIdWithMetaQueryObserver,
    useCouponQueryObserver,
    useRatePlanQueryObserver,
    useTotalToBePaidQueryObserver
} from "../../../../api/services";
import DialogCloseButton from "../../../../components/DialogCloseButton";
import { setSnackbar } from "../../../../../../util/state/globalAction";
import { snackbarTypes } from "../../../../../../util/state/globalVariables";
import useDialog from "../../../../hooks/useDialog";
import { LOCALE_DATE_FORMAT, LOCALE_DATE_TIME_FORMAT } from "../../../../utils";
import { IFormValues } from "../../types";
import { fullEditBooking } from "../../../../api/services";
import ConfirmDialog from "../confirm-dialog/ConfirmDialog";
import { useRecoilValue } from "recoil";
import { editIsPersonBAState, recalculationTotalToBePaid } from "../../recoil";

const ButtonBookFullEdit = (props: {bookingId: string | undefined}) => {

    const { t } = useTranslation('booking');

    const { dispatchGlobalData, globalData } = useContext(GlobalContext);

    const navigate = useNavigate();

    const queryClient = useQueryClient();

    const bookingId = props.bookingId;

    const formContext = useFormContext<IFormValues>();
    const tenant = useWatch({ control: formContext.control, name: "basicDetails.tenant" });
    const dealer = useWatch({ control: formContext.control, name: "basicDetails.dealer" });

    const recalculateTotalToBePaid  = useRecoilValue(recalculationTotalToBePaid);
    const isPersonBA = useRecoilValue(editIsPersonBAState);

    const couponQuery = useCouponQueryObserver({tenant: tenant as string, dealer: dealer as string});
    const ratePlanQuery = useRatePlanQueryObserver({tenant: tenant as string, dealer: dealer as string});
    const totalToBePaidQuery = useTotalToBePaidQueryObserver({tenant: tenant as string, dealer: dealer as string});
    const bookingQuery = useBookingByIdWithMetaQueryObserver(bookingId);

    const confirmBook = useCallback(async () => {
        const data = formContext.getValues();
        if (
            !data.basicDetails.pickupLocation ||
            !data.basicDetails.dropoffLocation ||
            !data.basicDetails.carGroup ||
            !data.basicDetails.mileageIncluded ||
            !bookingId ||
            !ratePlanQuery.data ||
            !totalToBePaidQuery.data
        ) {
            return;
        }

        const productAffiliatedQuery = queryClient.getQueryData<IGetCompanyReturn>([ReactQueryKey.Product, {segment: data.basicDetails.rentalType, tenant: tenant as string, dealer: dealer as string}]);

        await bookMutation.mutateAsync({
            id: bookingId,
            confirmData: {
                firstDriver: {
                    customerId: data.firstDriver.id,
                    name: data.firstDriver.name,
                    surname: data.firstDriver.surname,
                    birthDate: data.firstDriver.birthDate ? data.firstDriver.birthDate.format(LOCALE_DATE_FORMAT) : '',
                    birthNation: data.firstDriver.birthNation ?? '',
                    birthPlace: data.firstDriver.birthPlace ?? '',
                    residenceCountry: data.firstDriver.residenceCountry ?? '',
                    fiscalCode: data.firstDriver.fiscalCode,
                    drivingLicenseNumber: data.firstDriver.drivingLicenseNumber,
                    email: data.firstDriver.email,
                    mobile: data.firstDriver.mobile,
                    mobilePrefix: data.firstDriver.mobilePrefix || null,
                    gender: data.firstDriver.gender ?? ''
                },
                bookingOwner: data.basicDetails.rentalType === RentalType.RETAIL || isPersonBA ?
                    {
                        customerId: data.bookingOwner.id,
                        name: data.bookingOwner.name,
                        surname: data.bookingOwner.surname,
                        birthDate: data.bookingOwner.birthDate ? data.bookingOwner.birthDate.format(LOCALE_DATE_FORMAT) : '',
                        birthNation: data.bookingOwner.birthNation ?? '',
                        birthPlace: data.bookingOwner.birthPlace ?? '',
                        residenceCountry: data.bookingOwner.residenceCountry ?? '',
                        fiscalCode: data.bookingOwner.fiscalCode,
                        drivingLicenseNumber: data.bookingOwner.drivingLicenseNumber,
                        email: data.bookingOwner.email,
                        mobile: data.bookingOwner.mobile,
                        mobilePrefix: data.bookingOwner.mobilePrefix || null
                    } : null,
                bookingCompanyOwner: data.basicDetails.rentalType !== RentalType.RETAIL && !isPersonBA ?
                    {
                        customerId: data.bookingOwner.id,
                        companyName: data.bookingOwner.companyName,
                        country: data.bookingOwner.country,
                        city: data.bookingOwner.city,
                        streetName: data.bookingOwner.streetName,
                        streetNumber: data.bookingOwner.streetNumber,
                        postalCode: data.bookingOwner.postalCode,
                        email: data.bookingOwner.email,
                        pec: data.bookingOwner.certifiedEmail,
                        telephone: data.bookingOwner.telephone,
                        mobile: data.bookingOwner.mobile,
                        mobilePrefix: data.bookingOwner.mobilePrefix || null,
                        vatNumber: data.bookingOwner.vatNumber,
                        vatNumberCountry: data.bookingOwner.vatNumberCountry,
                        fiscalCode: data.bookingOwner.fiscalCode,
                        customerCode: data.basicDetails.customerCode ?? '',
                        sdiCode: data.bookingOwner.invoiceCode,
                        sapId: '',
                        legalEntity: data.bookingOwner.legalEntity,
                        publicAuthority: data.bookingOwner.publicAuthority ? '1' : '0',
                        paIdNumber: data.bookingOwner.publicAuthority ? data.bookingOwner.paIdNumber : '',
                        siren: data.bookingOwner.siren,
                        siret: data.bookingOwner.siret,
                    } : null,
                invoice: {
                    customerId: data.invoice.id,
                    type: data.invoice.type,
                    name: data.invoice.name,
                    surname: data.invoice.surname,
                    birthDate: data.invoice.birthDate ? data.invoice.birthDate.format(LOCALE_DATE_FORMAT) : '',
                    birthNation: data.invoice.birthNation ?? '',
                    birthPlace: data.invoice.birthPlace ?? '',
                    residenceCountry: data.invoice.residenceCountry ?? '',
                    fiscalCode: data.invoice.fiscalCode,
                    email: data.invoice.email,
                    mobile: data.invoice.mobile,
                    drivingLicenseNumber: data.invoice.drivingLicenseNumber,
                    billingCountry: data.invoice.billingCountry ?? '',
                    billingCity: data.invoice.billingCity,
                    billingStreetName: data.invoice.billingStreetName,
                    billingStreetNumber: data.invoice.billingStreetNumber,
                    billingPostalCode: data.invoice.billingPostalCode,
                    pec: data.invoice.certifiedEmail,
                    vatNumber: data.invoice.vatNumber,
                    vatNumberCountry: data.invoice.vatNumberCountry,
                    invoiceCode: data.invoice.invoiceCode,
                    companyName: data.invoice.companyName,
                    mobilePrefix: data.invoice.mobilePrefix || null,
                    billingProvince: data.invoice.billingProvince ?? '',
                    legalEntity: data.invoice.legalEntity,
                    publicAuthority: data.invoice.publicAuthority ? '1' : '0',
                    paIdNumber: data.invoice.publicAuthority ? data.invoice.paIdNumber : '',
                    siren: data.invoice.siren,
                    siret: data.invoice.siret,
                },
                additionalDrivers: data.additionalDrivers.map(ad => ({
                    customerId: ad.id,
                    name: ad.name,
                    surname: ad.surname,
                    email: ad.email
                })),
            },
            draftData: {
                rentalType: data.basicDetails.rentalType,
                segment: productAffiliatedQuery?.segmentCode || '',
                paymentCodeDescription: bookingQuery?.data?.booking?.paymentCodeDescription ?? '',
                customerCode: data.basicDetails.customerCode || '',
                cig: data.basicDetails.cig || '',
                pickupLocation: data.basicDetails.pickupLocation,
                dropoffLocation: data.basicDetails.dropoffLocation,
                pickupDateLocalTime: data.basicDetails.pickupDate?.format(LOCALE_DATE_TIME_FORMAT) || '',
                dropoffDateLocalTime: data.basicDetails.dropoffDate?.format(LOCALE_DATE_TIME_FORMAT) || '',
                carGroup: data.basicDetails.carGroup,
                mileageIncluded: data.basicDetails.mileageIncluded,
                ancillaries: Object
                    .entries(data.extraServices.ancillary)
                    .reduce((arr: any[], [key, value]: [string, number | boolean]) => {
                        const currAncillary = ratePlanQuery.data?.ancillaries.find(a => {
                            if (key === AncillarySubtype.AdditionalDrivers) {
                                return a.subtype === AncillarySubtype.AdditionalDrivers;
                            }
                            return a.ancillaryCode === key;
                        });
                        switch (typeof value) {
                            case 'number':
                                arr.push({
                                    ancillaryCode: currAncillary?.ancillaryCode ?? '',
                                    units: currAncillary?.mileageNeeded ? null : value,
                                    mileage: currAncillary?.mileageNeeded ? value : null,
                                    selected: value > 0,
                                    label: currAncillary?.name.defaultLabel,
                                    subtype: currAncillary?.subtype ?? 'NONE',
                                    sellType: currAncillary?.ancillaryDetails[0]?.sellType ?? '',
                                    passageToCounterNeeded: currAncillary?.passageToCounterNeeded == undefined || currAncillary?.passageToCounterNeeded == null ? false : currAncillary?.passageToCounterNeeded
                                });
                                break;
                            case 'boolean':
                                arr.push({
                                    ancillaryCode: currAncillary?.ancillaryCode ?? '',
                                    units: 1,
                                    mileage: null,
                                    selected: value,
                                    label: currAncillary?.name.defaultLabel,
                                    subtype: currAncillary?.subtype ?? 'NONE',
                                    sellType: currAncillary?.ancillaryDetails[0]?.sellType ?? '',
                                    passageToCounterNeeded: currAncillary?.passageToCounterNeeded == undefined || currAncillary?.passageToCounterNeeded == null ? false : currAncillary?.passageToCounterNeeded
                                });
                                break;
                        }
                        return arr;
                    }, []),
                collectionStreetName: data.extraServices.collectionStreetName,
                collectionStreetNumber: data.extraServices.collectionStreetNumber,
                collectionPostalCode: data.extraServices.collectionPostalCode,
                collectionCity: data.extraServices.collectionCity,
                collectionState: data.extraServices.collectionState,
                deliveryStreetName: data.extraServices.deliveryStreetName,
                deliveryStreetNumber: data.extraServices.deliveryStreetNumber,
                deliveryPostalCode: data.extraServices.deliveryPostalCode,
                deliveryCity: data.extraServices.deliveryCity,
                deliveryState: data.extraServices.deliveryState,
                discountCode: data.extraServices.discountType === 'promo' && data.extraServices.discountCode  ? data.extraServices.discountCode : undefined,
                discountOverprice: data.extraServices.discountOverprice || undefined,
                note: data.extraServices.note,
                totals: [],
                dossierNumber: data.basicDetails.dossierNumber ?? '',
                reservedTariff: !!productAffiliatedQuery?.reservedTariff,
                affiliationCode: data.extraServices.affiliationCode,
                flightNumber: data.extraServices.flightNumber,
                totalToBePaid: 0,
                tenant: globalData.user.legalEntities[0].name ?? '',
                dealer: globalData.user.dealers[0].name ?? '',
                fcaBankCardNumber: data.extraServices.discountType === 'fca' && data.extraServices.fcaBankCardNumber ? data.extraServices.fcaBankCardNumber : undefined,
                products: ratePlanQuery.data.mileageList
                    .find(m => m.value === data.basicDetails.mileageIncluded)?.products
                    .map(p => ({
                        product: p.product,
                        rateId: p.rateId,
                        rateName: p.rateName
                    })) ?? [],
                recovery: !!bookingQuery?.data?.booking.recovery
            },
            total: totalToBePaidQuery.data
        });
    }, [bookingId, totalToBePaidQuery.data, ratePlanQuery.data, isPersonBA]);

    const bookMutation = useMutation(
        fullEditBooking,
        {
            onSuccess: (data) => {
                const response = data.data;
                if (response.blackList) {
                    confirmationDialog.handleClose();
                    blacklistErrorDialog.handleShow();
                    return;
                }
                dispatchGlobalData({
                    type: setSnackbar,
                    payload: {
                        snackbar: {
                            message: (
                                t('message.info.new_booking') +
                                t(`status.${response.status}`)) +
                                (response.subStatus ? ` - ${t(`substatus.${response.subStatus}`)}` : '')
                            ,
                            type: response.status === BookingStatus.pending ? snackbarTypes.warning : snackbarTypes.info,
                            show: true,
                            autoClose: true,
                        }
                    }
                });
                navigate(`/booking/view/${response.id}`);
            },
            onError: (err) => {
                let message = '';

                if (err instanceof (AxiosError)) {
                    switch (err.response?.data.code) {
                        case '1':
                            message = t('message.error.main_driver_fiscal_code');
                            formContext.setError(
                                'firstDriver.fiscalCode',
                                {
                                    message: 'message.validation.invalid_fiscal_code'
                                }
                            )
                            break;
                        case '2':
                            message = t('message.error.billing_owner_fiscal_code');
                            formContext.setError(
                                'invoice.fiscalCode',
                                {
                                    message: 'message.validation.invalid_fiscal_code'
                                }
                            )
                            break;
                        case '3':
                            message = t('message.error.booking_owner_fiscal_code');
                            formContext.setError(
                                'bookingOwner.fiscalCode',
                                {
                                    message: 'message.validation.invalid_fiscal_code'
                                }
                            )
                            break;
                        case '4':
                            message = t('message.error.discount_code');

                            formContext.setError(
                                'extraServices.discountCode',
                                {
                                    message: 'message.validation.invalid_discount_code'
                                }
                            )
                            break;
                        case '403':
                            message = t('message.error.not_permitted_editable_booking');
                            break;
                        case '10':
                            message = t('message.error.rate_not_configured');
                            break;
                        case '50':
                            message = t('message.error.maximum_number_rentals');
                            break;
                        case '400':
                            message = t('message.error.suspended_customers');
                            break;
                        default:
                            message = t('message.error.default');
                    }
                }
                dispatchGlobalData({
                    type: setSnackbar,
                    payload: {
                        snackbar: {
                            message: message,
                            type: snackbarTypes.error,
                            show: true,
                            autoClose: true,
                        }
                    }
                });
            }
        }
    );
    const checkStopSellMutation = useMutation(
        checkStopSell,
        {
            onSuccess: async (data) => {
                confirmationDialog.handleClose();
                if (!data.stopSell && !data.stopOverbooking) {
                    confirmBook();
                }
                else {
                    if (data.ignore || data.ignore === null) {
                        if (data.stopSell && data.stopOverbooking) {
                            setStopDialogMessage(t('message.warning.stop_sell_overbooking'));
                        }
                        else if (data.stopSell) {
                            setStopDialogMessage(t('message.warning.stop_sell'));
                        }
                        else {
                            setStopDialogMessage(t('message.warning.stop_overbooking'));
                        }
                        stopSellWarningDialog.handleShow();
                    }
                    else {
                        if (data.stopSell && data.stopOverbooking) {
                            setStopDialogMessage(t('message.error.stop_sell_overbooking'));
                        }
                        else if (data.stopSell) {
                            setStopDialogMessage(t('message.error.stop_sell'));
                        }
                        else {
                            setStopDialogMessage(t('message.error.stop_overbooking'));
                        }
                        stopSellErrorDialog.handleShow();
                    }
                }
            },
            onError: () => {
                dispatchGlobalData({
                    type: setSnackbar,
                    payload: {
                        snackbar: {
                            message: t('message.error.default'),
                            type: snackbarTypes.error,
                            show: true,
                            autoClose: true,
                        }
                    }
                });
            }
        }
    )

    const confirmationDialog = useDialog();
    const blacklistErrorDialog = useDialog();
    const stopSellErrorDialog = useDialog();
    const stopSellWarningDialog = useDialog();

    const [blacklistDialogDisabled, setBlacklistDialogDisabled] = useState<boolean>(false);
    const [stopDialogMessage, setStopDialogMessage] = useState<string>('');

    const handleSave = useCallback(async () => {
        if (!bookingId) {
            return;
        }

        const data = formContext.getValues();
        checkStopSellMutation.mutate({
            bookingId,
            pickupDate: data.basicDetails.pickupDate?.toISOString(),
            dropoffDate: data.basicDetails.dropoffDate?.toISOString()
        })
    }, [bookingId]);

    const onSubmit = useCallback(() => {
        if(recalculateTotalToBePaid) {
        confirmationDialog.handleShow();
        } else {
            confirmBook();
        }
    }, [bookingId, totalToBePaidQuery.data, ratePlanQuery.data]);

    const handleBlacklistDialogClose = useCallback(() => {
        setBlacklistDialogDisabled(true);
        navigate(0);
    }, []);

    return (
        <>
            <Button onClick={formContext.handleSubmit(onSubmit)}
                    disabled={
                        ratePlanQuery.isFetching ||
                        totalToBePaidQuery.isFetching
                    }>
                {t('label.update')}
            </Button>
            <ConfirmDialog
                title={t('label.book')}
                open={confirmationDialog.open}
                onClose={confirmationDialog.handleClose}
                onConfirm={handleSave}
            />
            <Dialog
                open={blacklistErrorDialog.open}
                fullWidth
                maxWidth="sm"
            >
                <DialogCloseButton
                    onClick={handleBlacklistDialogClose}
                    disabled={blacklistDialogDisabled}
                />
                <DialogTitle>
                    <Stack
                        direction="row"
                        alignItems="center"
                        columnGap={1}
                    >
                        <WarningRounded color="error" />
                        {t('label.error')}
                    </Stack>
                </DialogTitle>
                <DialogContent>
                    <DialogContentText
                        sx={{ whiteSpace: 'pre-line' }}
                    >
                        {t('message.error.insert_booking')}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={handleBlacklistDialogClose}
                        disabled={blacklistDialogDisabled}
                    >
                        {t('label.close')}
                    </Button>
                </DialogActions>
            </Dialog>
            <Dialog
                open={stopSellErrorDialog.open}
                onClose={stopSellErrorDialog.handleClose}
                fullWidth
                maxWidth="sm"
            >
                <DialogCloseButton
                    onClick={stopSellErrorDialog.handleClose}
                />
                <DialogTitle>
                    <Stack
                        direction="row"
                        alignItems="center"
                        columnGap={1}
                    >
                        <WarningRounded color="error" />
                        {t('label.error')}
                    </Stack>
                </DialogTitle>
                <DialogContent>
                    <DialogContentText
                        sx={{
                            whiteSpace: 'pre-line'
                        }}
                    >
                        {stopDialogMessage}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={stopSellErrorDialog.handleClose}
                    >
                        {t('label.ok')}
                    </Button>
                </DialogActions>
            </Dialog>
            <Dialog
                open={stopSellWarningDialog.open}
                onClose={stopSellWarningDialog.handleClose}
                fullWidth
                maxWidth="sm"
            >
                <DialogCloseButton
                    onClick={stopSellWarningDialog.handleClose}
                />
                <DialogTitle>
                    <Stack
                        direction="row"
                        alignItems="center"
                        columnGap={1}
                    >
                        <WarningRounded color="warning" />
                        {t('label.warning')}
                    </Stack>
                </DialogTitle>
                <DialogContent>
                    <DialogContentText
                        sx={{
                            whiteSpace: 'pre-line'
                        }}
                    >
                        {stopDialogMessage}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button
                        variant="outlined"
                        onClick={stopSellWarningDialog.handleClose}
                    >
                        {t('label.back')}
                    </Button>
                    <Button
                        onClick={confirmBook}
                    >
                        {t('label.save')}
                    </Button>
                </DialogActions>
            </Dialog>
            <CustomLoader
                loading={
                    checkStopSellMutation.isLoading ||
                    bookMutation.isLoading
                }
            />
        </>
    );
};

export default ButtonBookFullEdit;
